import { ReloadOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { t } from '@hefe/i18n';
import { BasketballEventType } from '@hefe/models';
import {
  currentUserIsAdminSelector,
  currentUserSelector,
  EventsApi,
  ModalsSlice,
} from '@hefe/store';
import { formatDateTime } from '@hefe/utils';
import {
  Button,
  Card,
  Col,
  Row,
  Skeleton,
  Space,
  Tabs,
  Typography,
} from 'antd';
import { Gutter } from 'antd/es/grid/row';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import EventDetailsAuditsComponent from '../event-details-audits/event-details-audits.component';
import EventDetailsRepliesComponent from '../event-details-replies/event-details-replies.component';

export default function EventDetailsComponent() {
  const eventModal = useSelector(ModalsSlice.modalsState).eventModal;
  const eventId = eventModal?.id;

  const user = useSelector(currentUserSelector);
  const currentUserIsAdmin = useSelector(currentUserIsAdminSelector);

  const {
    data: eventDetails,
    isFetching,
    refetch,
  } = EventsApi.useFetchBasketballEventQuery(eventId, {
    skip: !eventId,
  });

  const actionButtons = useMemo(() => {
    const returnValue = [];

    returnValue.push(
      <Button onClick={() => refetch()} icon={<ReloadOutlined />} />
    );

    return returnValue;
  }, [refetch]);

  const tabs = useMemo(() => {
    const returnValue = [];

    let enableUpdateReply = false;
    const teamId =
      eventDetails?.createable_type ===
      'App\\Models\\Basketball\\BasketballTeam'
        ? eventDetails?.createable_id
        : undefined;
    if (teamId) {
      enableUpdateReply =
        user?.basketball_user?.coached_teams?.findIndex(
          (team) => team.id === teamId
        ) !== -1;
    } else {
      enableUpdateReply = currentUserIsAdmin;
    }

    returnValue.push({
      label: t('replies'),
      key: 'replies',
      children: (
        <EventDetailsRepliesComponent
          eventId={eventId}
          eventType={BasketballEventType.EVENT}
          isCoach={enableUpdateReply}
          replies={eventDetails?.replies}
          allowViewReplies
          team={{
            id: 0,
            name: 'Club',
          }}
        />
      ),
    });

    returnValue.push({
      label: t('changelog'),
      key: '5',
      children: (
        <EventDetailsAuditsComponent audits={eventDetails?.audits ?? []} />
      ),
    });

    return returnValue;
  }, [
    currentUserIsAdmin,
    eventDetails?.audits,
    eventDetails?.createable_id,
    eventDetails?.createable_type,
    eventDetails?.replies,
    eventId,
    user?.basketball_user?.coached_teams,
  ]);

  const gutter = [16, 16] as [Gutter, Gutter];

  return (
    <Skeleton loading={isFetching}>
      <Row gutter={gutter}>
        <Col xs={24} lg={8} xxl={6}>
          <Row gutter={gutter}>
            <Col xs={24}>
              <Card>
                <PageHeader
                  title={eventDetails?.name}
                  extra={<Button.Group>{actionButtons}</Button.Group>}
                >
                  <Space direction="vertical" size={0}>
                    <Typography.Text>
                      {eventDetails?.canceled
                        ? t('canceled').toUpperCase()
                        : formatDateTime(eventDetails?.date ?? '', {
                            withWeekday: 'long',
                          })}
                    </Typography.Text>
                    <Typography.Text>{eventDetails?.location}</Typography.Text>
                  </Space>
                </PageHeader>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xs={24} lg={16} xxl={18}>
          <Card>
            <Tabs items={tabs} />
          </Card>
        </Col>
      </Row>
    </Skeleton>
  );
}
